'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import ScrollReveal from '@/components/ScrollReveal';
import { useCart } from '@/components/CartContext';

const product = {
  id: 'prod-001',
  name: 'Luminous Veil Serum',
  tagline: 'Brightening · Anti-Aging · Radiance',
  category: 'Skincare',
  price: 89,
  rating: 4.9,
  reviews: 1204,
  badge: 'Bestseller',
  description:
  'Our most celebrated formula. The Luminous Veil Serum combines stabilized 15% Vitamin C with 0.3% encapsulated retinol and Icelandic algae extract to deliver visible brightening, fine line reduction, and an undeniable glow — all in a single step.',
  images: [
  {
    src: 'https://images.unsplash.com/photo-1576426863848-c21f53c60b19',
    alt: 'LuxeBeauty Luminous Veil Serum amber dropper bottle on white marble with soft natural lighting'
  },
  {
    src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b79a0104-1770469350963.png',
    alt: 'Luminous Veil Serum texture close-up showing golden vitamin C formula on fingertip'
  },
  {
    src: 'https://img.rocket.new/generatedImages/rocket_gen_img_102939d59-1768149384457.png',
    alt: 'LuxeBeauty serum packaging detail showing ingredient list and gold foil branding'
  },
  {
    src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e1e4672a-1769381402528.png',
    alt: 'Woman applying LuxeBeauty Luminous Veil Serum to glowing skin in morning light routine'
  }],


  sizes: [
  { label: '15ml', price: 45, note: 'Travel' },
  { label: '30ml', price: 89, note: 'Standard' },
  { label: '50ml', price: 129, note: 'Best Value' }],


  keyIngredients: [
  { name: '15% Vitamin C', benefit: 'Brightens, protects, collagen synthesis' },
  { name: '0.3% Retinol', benefit: 'Reduces fine lines, speeds cell turnover' },
  { name: 'Icelandic Algae', benefit: 'Cellular repair and deep hydration' },
  { name: 'Ferulic Acid', benefit: 'Stabilizes Vitamin C, doubles antioxidant effect' },
  { name: 'Hyaluronic Acid', benefit: 'Plumps and retains moisture' }],


  fullIngredients:
  'Aqua, Ascorbic Acid (15%), Propanediol, Glycerin, Ferulic Acid, Retinol (0.3%), Algae Extract (Icelandic), Sodium Hyaluronate, Niacinamide, Panthenol, Tocopherol, Xanthan Gum, Sodium Hydroxide, Phenoxyethanol, Ethylhexylglycerin.',
  howToUse: [
  'Apply 3–4 drops to cleansed, dry skin.',
  'Gently press into face, neck, and décolleté.',
  'Follow with your moisturizer.',
  'Use AM and PM. Always apply SPF in the morning.',
  'Results visible in 2–4 weeks with consistent use.'],


  claims: [
  { value: '70%', label: 'Reduction in hyperpigmentation' },
  { value: '6 wks', label: 'Average time to visible results' },
  { value: '94%', label: 'Reported improvement in skin radiance' },
  { value: '1,204', label: '5-star reviews' }]

};

const reviews = [
{
  id: 'rev-1',
  name: 'Priya S.',
  location: 'New York, NY',
  rating: 5,
  date: 'Jan 14, 2026',
  title: 'Transformed my skin in 6 weeks',
  body: 'I have had stubborn melasma for years. Nothing worked until this. At week 6 my dermatologist actually asked what I had changed. I will never be without this.',
  verified: true,
  helpful: 142
},
{
  id: 'rev-2',
  name: 'Jennifer W.',
  location: 'Chicago, IL',
  rating: 5,
  date: 'Feb 2, 2026',
  title: 'Worth every penny',
  body: 'I was skeptical about the price but one bottle lasts two months. The texture is incredible — absorbs instantly, no stickiness. My skin looks lit from within.',
  verified: true,
  helpful: 98
},
{
  id: 'rev-3',
  name: 'Amara O.',
  location: 'Los Angeles, CA',
  rating: 5,
  date: 'Jan 28, 2026',
  title: 'My holy grail serum',
  body: "I've tried La Mer, SkinCeuticals, and Tatcha. This beats them all. The combination of Vitamin C and retinol in one step is genius and my skin has never looked better.",
  verified: true,
  helpful: 87
}];


export default function ProductDetailClient() {
  const [activeImage, setActiveImage] = useState(0);
  const [activeSize, setActiveSize] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'ingredients' | 'how-to' | 'claims'>('claims');
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const { addItem } = useCart();

  const selectedSize = product.sizes[activeSize];

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: selectedSize.price,
      image: product.images[0].src,
      variant: selectedSize.label,
      category: product.category
    });
  };

  const toggleAccordion = (key: string) => {
    setOpenAccordion(openAccordion === key ? null : key);
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Breadcrumb */}
      <div className="max-w-8xl mx-auto px-6 lg:px-10 py-4">
        <div className="flex items-center gap-2 text-[11px] text-[#8A7E72]">
          <Link href="/homepage" className="hover:text-[#C9A96E] transition-colors">
            Home
          </Link>
          <span>/</span>
          <Link href="/products" className="hover:text-[#C9A96E] transition-colors">
            Shop
          </Link>
          <span>/</span>
          <Link href="/products" className="hover:text-[#C9A96E] transition-colors">
            Skincare
          </Link>
          <span>/</span>
          <span className="text-[#1A1612] font-medium">{product.name}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-8xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Image Gallery */}
          <div className="col-span-12 lg:col-span-6">
            {/* Main Image */}
            <div
              className="relative overflow-hidden mb-4 bg-[#F0EBE0]"
              style={{ aspectRatio: '4/5' }}>
              
              <AppImage
                src={product.images[activeImage].src}
                alt={product.images[activeImage].alt}
                className="w-full h-full object-cover"
                priority />
              

              {/* Badge */}
              <div
                className="absolute top-5 left-5 px-3 py-1.5 text-[9px] font-bold tracking-widest uppercase"
                style={{ background: '#1A1612', color: '#FAF8F3' }}>
                
                {product.badge}
              </div>
            </div>

            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-3">
              {product.images.map((img, i) =>
              <button
                key={`thumb-${i}`}
                onClick={() => setActiveImage(i)}
                className={`relative overflow-hidden transition-all ${
                i === activeImage ?
                'ring-2 ring-[#C9A96E] ring-offset-2 ring-offset-[#FAF8F3]' :
                'opacity-60 hover:opacity-100'}`
                }
                style={{ aspectRatio: '1/1' }}
                aria-label={`View product image ${i + 1}`}>
                
                  <AppImage
                  src={img.src}
                  alt={`Product thumbnail ${i + 1}`}
                  className="w-full h-full object-cover" />
                
                </button>
              )}
            </div>
          </div>

          {/* Right: Product Info */}
          <div className="col-span-12 lg:col-span-6">
            {/* Header */}
            <div className="mb-6">
              <p className="label-gold mb-2">
                {product.category} · {product.tagline}
              </p>
              <h1
                className="font-fraunces text-[#1A1612] leading-tight mb-4"
                style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400 }}>
                
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) =>
                  <span key={s} className="text-[#C9A96E] text-base">
                      ★
                    </span>
                  )}
                </div>
                <span className="text-[13px] text-[#8A7E72] font-medium">
                  {product.rating} · {product.reviews.toLocaleString()} reviews
                </span>
                <a
                  href="#reviews"
                  className="text-[11px] text-[#C9A96E] underline underline-offset-2 hover:text-[#A07840] transition-colors">
                  
                  Read reviews
                </a>
              </div>

              <p className="text-[15px] text-[#8A7E72] leading-relaxed">{product.description}</p>
            </div>

            <div className="divider-gold mb-6" />

            {/* Claims Quick Stats */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {product.claims.map((claim) =>
              <div
                key={claim.label}
                className="p-4 text-center"
                style={{
                  background: 'rgba(201,169,110,0.06)',
                  border: '1px solid rgba(201,169,110,0.15)'
                }}>
                
                  <p className="font-fraunces text-[22px] font-medium text-[#C9A96E]">
                    {claim.value}
                  </p>
                  <p className="text-[10px] text-[#8A7E72] leading-tight mt-0.5">{claim.label}</p>
                </div>
              )}
            </div>

            {/* Size Selector */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[12px] font-semibold tracking-wider uppercase text-[#1A1612]">
                  Size
                </p>
                <p className="text-[11px] text-[#C9A96E] font-medium">{selectedSize.note}</p>
              </div>
              <div className="flex gap-3">
                {product.sizes.map((size, i) =>
                <button
                  key={size.label}
                  onClick={() => setActiveSize(i)}
                  className={`flex-1 py-3 text-[12px] font-semibold transition-all border ${
                  i === activeSize ?
                  'bg-[#1A1612] text-[#FAF8F3] border-[#1A1612]' :
                  'bg-transparent text-[#8A7E72] border-[rgba(26,22,18,0.15)] hover:border-[#1A1612] hover:text-[#1A1612]'}`
                  }>
                  
                    {size.label}
                    <br />
                    <span className="text-[10px] opacity-70">${size.price}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Quantity + Add to Cart */}
            <div className="flex gap-3 mb-6">
              {/* Quantity */}
              <div className="flex items-center border border-[rgba(26,22,18,0.12)]">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="qty-btn"
                  aria-label="Decrease quantity">
                  
                  −
                </button>
                <span className="w-10 text-center text-[14px] font-semibold text-[#1A1612]">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="qty-btn"
                  aria-label="Increase quantity">
                  
                  +
                </button>
              </div>

              {/* Add to Cart */}
              <button
                onClick={handleAddToCart}
                className="btn-gold flex-1 flex items-center justify-center gap-2">
                
                <Icon name="ShoppingBagIcon" size={16} />
                Add to Cart · ${(selectedSize.price * quantity).toLocaleString()}
              </button>
            </div>

            {/* Secondary Actions */}
            <div className="flex gap-3 mb-8">
              <Link href="/checkout" className="flex-1">
                <button className="btn-dark w-full">Buy Now</button>
              </Link>
              <button
                className="w-12 h-12 border border-[rgba(26,22,18,0.12)] flex items-center justify-center hover:border-red-300 hover:text-red-400 transition-all"
                aria-label="Add to wishlist">
                
                <Icon name="HeartIcon" size={18} />
              </button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 mb-8">
              {[
              { icon: 'CheckBadgeIcon', text: 'Clinically Tested' },
              { icon: 'LeafIcon' as 'StarIcon', text: 'Cruelty-Free' },
              { icon: 'ArrowPathIcon', text: '30-Day Returns' },
              { icon: 'TruckIcon', text: 'Free Shipping $75+' }].
              map((badge) =>
              <div key={badge.text} className="flex items-center gap-1.5">
                  <Icon
                  name={badge.icon as 'CheckBadgeIcon'}
                  size={14}
                  className="text-[#C9A96E]" />
                

                  <span className="text-[11px] text-[#8A7E72] font-medium">{badge.text}</span>
                </div>
              )}
            </div>

            <div className="divider-gold mb-6" />

            {/* Tabs: Ingredients / How To Use / Claims */}
            <div>
              <div className="flex gap-0 mb-6 border-b border-[rgba(26,22,18,0.08)]">
                {(['claims', 'ingredients', 'how-to'] as const).map((tab) =>
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-[11px] font-bold tracking-wider uppercase transition-all border-b-2 -mb-px ${
                  activeTab === tab ?
                  'border-[#C9A96E] text-[#1A1612]' :
                  'border-transparent text-[#8A7E72] hover:text-[#1A1612]'}`
                  }>
                  
                    {tab === 'claims' ? 'Results' : tab === 'how-to' ? 'How to Use' : 'Ingredients'}
                  </button>
                )}
              </div>

              {activeTab === 'claims' &&
              <div className="space-y-3">
                  {product.keyIngredients.map((ing) =>
                <div key={ing.name} className="flex items-start gap-3">
                      <span className="text-[#C9A96E] mt-0.5">✦</span>
                      <div>
                        <span className="text-[13px] font-semibold text-[#1A1612]">
                          {ing.name} —{' '}
                        </span>
                        <span className="text-[13px] text-[#8A7E72]">{ing.benefit}</span>
                      </div>
                    </div>
                )}
                </div>
              }

              {activeTab === 'ingredients' &&
              <div>
                  <p className="text-[13px] text-[#8A7E72] leading-relaxed mb-4">
                    {product.fullIngredients}
                  </p>
                  <p className="text-[11px] text-[#C9A96E] font-medium">
                    Free from: Parabens · Sulfates · Synthetic Fragrance · Mineral Oil · PEGs
                  </p>
                </div>
              }

              {activeTab === 'how-to' &&
              <ol className="space-y-3">
                  {product.howToUse.map((step, i) =>
                <li key={`step-${i}`} className="flex items-start gap-3">
                      <span
                    className="font-fraunces text-[#C9A96E] opacity-60 flex-shrink-0 leading-none"
                    style={{ fontSize: '1.2rem', marginTop: '-2px' }}>
                    
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <p className="text-[13px] text-[#8A7E72] leading-relaxed">{step}</p>
                    </li>
                )}
                </ol>
              }
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div
        id="reviews"
        className="max-w-8xl mx-auto px-6 lg:px-10 py-16 border-t border-[rgba(201,169,110,0.12)]">
        
        <ScrollReveal>
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-px bg-[#C9A96E]" />
                <span className="label-gold">Customer Reviews</span>
              </div>
              <div className="flex items-end gap-4">
                <span
                  className="font-fraunces text-[#1A1612] leading-none"
                  style={{ fontSize: '4rem', fontWeight: 400 }}>
                  
                  4.9
                </span>
                <div className="pb-2">
                  <div className="flex gap-0.5 mb-1">
                    {[1, 2, 3, 4, 5].map((s) =>
                    <span key={s} className="text-[#C9A96E] text-lg">
                        ★
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-[#8A7E72]">Based on 1,204 reviews</p>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((review, index) =>
          <ScrollReveal key={review.id} delay={index * 80}>
              <div
              className="p-6 lg:p-8 h-full"
              style={{
                background: 'white',
                border: '1px solid rgba(201,169,110,0.1)',
                borderTop: '2px solid rgba(201,169,110,0.3)'
              }}>
              
                <div className="flex gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((s) =>
                <span key={s} className="text-[#C9A96E] text-sm">
                      ★
                    </span>
                )}
                </div>
                <h4 className="font-semibold text-[14px] text-[#1A1612] mb-2">{review.title}</h4>
                <p className="text-[13px] text-[#8A7E72] leading-relaxed mb-4">
                  &ldquo;{review.body}&rdquo;
                </p>
                <div className="flex items-center justify-between mt-auto">
                  <div>
                    <p className="text-[12px] font-semibold text-[#1A1612]">{review.name}</p>
                    <p className="text-[10px] text-[#B8AFA6]">
                      {review.location} · {review.date}
                    </p>
                  </div>
                  {review.verified &&
                <span
                  className="text-[9px] font-bold tracking-widest uppercase px-2 py-1"
                  style={{ background: 'rgba(201,169,110,0.1)', color: '#C9A96E' }}>
                  
                      Verified
                    </span>
                }
                </div>
              </div>
            </ScrollReveal>
          )}
        </div>
      </div>
    </div>);

}